/**
 * Network Security Search Utilities
 * 
 * Resolves search queries (subnet, IP, ENI/VNIC, NACL, VPC/VCN, SG/NSG name) to related 
 * Security Group/NSG IDs to filter security findings accordingly.
 * 
 * Supports both AWS and OCI discovery formats.
 */

export type ResourceSearchType = 'subnet' | 'ip' | 'eni' | 'sg' | 'nacl' | 'vpc' | 'name';

/**
 * Identifies the type of resource being searched based on the query pattern
 */
export function identifyResourceType(query: string): ResourceSearchType {
  const trimmed = query.trim();
  
  // AWS patterns
  if (trimmed.startsWith('subnet-')) return 'subnet';
  if (trimmed.startsWith('eni-')) return 'eni';
  if (trimmed.startsWith('sg-')) return 'sg';
  if (trimmed.startsWith('acl-')) return 'nacl';
  if (trimmed.startsWith('vpc-')) return 'vpc';
  
  // OCI patterns (OCIDs)
  if (trimmed.includes('ocid1.subnet.')) return 'subnet';
  if (trimmed.includes('ocid1.vnic.')) return 'eni';
  if (trimmed.includes('ocid1.networksecuritygroup.')) return 'sg';
  if (trimmed.includes('ocid1.securitylist.')) return 'nacl'; // Security Lists are similar to NACLs
  if (trimmed.includes('ocid1.vcn.')) return 'vpc';
  
  // IP address pattern (IPv4)
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(trimmed)) return 'ip';
  
  return 'name';
}

// ============================================================================
// AWS Types
// ============================================================================
interface AWSSubnet {
  id: string;
  tags?: { Name?: string; [key: string]: string | undefined };
  cidr_block: string;
  vpc_id: string;
}

interface AWSSecurityGroup {
  id: string;
  group_name: string;
  vpc_id: string;
  description?: string;
  tags?: { Name?: string; [key: string]: string | undefined };
}

interface AWSENI {
  id: string;
  subnet_id: string;
  private_ip_address: string;
  private_ip_addresses?: Array<{ PrivateIpAddress: string }>;
  security_groups: Array<{ GroupId: string }> | string[];
}

interface AWSNetworkACL {
  id: string;
  associations: Array<{ SubnetId: string }>;
}

interface AWSVPC {
  info: { id: string; tags?: { Name?: string }; cidr_block: string };
  subnets: AWSSubnet[];
  security_groups: AWSSecurityGroup[];
  network_interfaces: AWSENI[];
  network_acls: AWSNetworkACL[];
}

// ============================================================================
// OCI Types
// ============================================================================
interface OCISubnet {
  id: string;
  display_name?: string;
  tags?: { [key: string]: string | undefined };
  cidr_block: string;
  vcn_id: string;
  security_list_ids?: string[];
}

interface OCINSG {
  id: string;
  display_name?: string;
  tags?: { [key: string]: string | undefined };
  vcn_id: string;
}

interface OCISecurityList {
  id: string;
  display_name?: string;
  tags?: { [key: string]: string | undefined };
  vcn_id: string;
}

interface OCIVNIC {
  id: string;
  display_name?: string;
  subnet_id: string;
  private_ip_address: string;
  private_ip_addresses?: Array<{ private_ip_address: string }>;
  nsg_ids?: string[];
}

interface OCIVCN {
  info: { id: string; display_name?: string; tags?: { Name?: string }; cidr_block?: string; cidr_blocks?: string[] };
  subnets: OCISubnet[];
  network_security_groups?: OCINSG[];
  security_lists?: OCISecurityList[];
  vnics?: OCIVNIC[];
}

// ============================================================================
// Unified Discovery Data Type
// ============================================================================
export interface DiscoveryData {
  vpcs?: Record<string, AWSVPC>;
  vcns?: Record<string, OCIVCN>;
}

export interface SearchResolutionResult {
  sgIds: string[];
  resolvedFrom: {
    type: ResourceSearchType;
    matchedResources: Array<{
      type: string;
      id: string;
      name?: string;
    }>;
  };
}

/**
 * Extracts security group IDs from an AWS ENI's security_groups field,
 * handling both { GroupId: string } format and plain string[] format
 */
function extractSgIdsFromAWSENI(eni: AWSENI): string[] {
  if (!eni.security_groups || !Array.isArray(eni.security_groups)) {
    return [];
  }
  
  return eni.security_groups.map(sg => {
    if (typeof sg === 'string') return sg;
    if (typeof sg === 'object' && sg.GroupId) return sg.GroupId;
    return '';
  }).filter(Boolean);
}

/**
 * Extracts NSG IDs from an OCI VNIC's nsg_ids field
 */
function extractNsgIdsFromOCIVNIC(vnic: OCIVNIC): string[] {
  if (!vnic.nsg_ids || !Array.isArray(vnic.nsg_ids)) {
    return [];
  }
  return vnic.nsg_ids.filter(Boolean);
}

/**
 * Resolves AWS search query to Security Group IDs
 */
function resolveAWSSearch(
  query: string,
  queryLower: string,
  type: ResourceSearchType,
  vpcs: Record<string, AWSVPC>,
  result: SearchResolutionResult
): void {
  const relatedSgIds = new Set<string>();

  for (const vpc of Object.values(vpcs)) {
    const subnets = vpc.subnets || [];
    const securityGroups = vpc.security_groups || [];
    const networkInterfaces = vpc.network_interfaces || [];
    const networkAcls = vpc.network_acls || [];

    switch (type) {
      case 'subnet':
      case 'name': {
        // Find subnets that match by ID, name tag, or CIDR
        const matchingSubnets = subnets.filter(s => {
          const subnetName = s.tags?.Name?.toLowerCase() || '';
          return (
            s.id === query ||
            s.id.toLowerCase() === queryLower ||
            subnetName.includes(queryLower) ||
            s.cidr_block.includes(query)
          );
        });

        matchingSubnets.forEach(subnet => {
          result.resolvedFrom.matchedResources.push({
            type: 'subnet',
            id: subnet.id,
            name: subnet.tags?.Name
          });
        });

        for (const subnet of matchingSubnets) {
          networkInterfaces
            .filter(eni => eni.subnet_id === subnet.id)
            .forEach(eni => {
              extractSgIdsFromAWSENI(eni).forEach(sgId => relatedSgIds.add(sgId));
            });
        }
        break;
      }

      case 'ip': {
        networkInterfaces
          .filter(eni => {
            if (eni.private_ip_address === query) return true;
            if (eni.private_ip_addresses) {
              return eni.private_ip_addresses.some(
                pip => pip.PrivateIpAddress === query
              );
            }
            return false;
          })
          .forEach(eni => {
            result.resolvedFrom.matchedResources.push({
              type: 'eni',
              id: eni.id,
              name: `IP: ${eni.private_ip_address}`
            });
            extractSgIdsFromAWSENI(eni).forEach(sgId => relatedSgIds.add(sgId));
          });
        break;
      }

      case 'eni': {
        networkInterfaces
          .filter(eni => eni.id === query || eni.id.toLowerCase() === queryLower)
          .forEach(eni => {
            result.resolvedFrom.matchedResources.push({
              type: 'eni',
              id: eni.id
            });
            extractSgIdsFromAWSENI(eni).forEach(sgId => relatedSgIds.add(sgId));
          });
        break;
      }

      case 'sg': {
        const matchingSg = securityGroups.find(
          sg => sg.id === query || sg.id.toLowerCase() === queryLower
        );
        if (matchingSg) {
          result.resolvedFrom.matchedResources.push({
            type: 'security_group',
            id: matchingSg.id,
            name: matchingSg.group_name
          });
          relatedSgIds.add(matchingSg.id);
        }
        break;
      }

      case 'nacl': {
        const nacl = networkAcls.find(
          n => n.id === query || n.id.toLowerCase() === queryLower
        );
        if (nacl) {
          result.resolvedFrom.matchedResources.push({
            type: 'nacl',
            id: nacl.id
          });
          
          const subnetIds = nacl.associations.map(a => a.SubnetId);
          
          networkInterfaces
            .filter(eni => subnetIds.includes(eni.subnet_id))
            .forEach(eni => {
              extractSgIdsFromAWSENI(eni).forEach(sgId => relatedSgIds.add(sgId));
            });
        }
        break;
      }

      case 'vpc': {
        if (vpc.info.id === query || vpc.info.id.toLowerCase() === queryLower) {
          result.resolvedFrom.matchedResources.push({
            type: 'vpc',
            id: vpc.info.id,
            name: vpc.info.tags?.Name
          });
          securityGroups.forEach(sg => relatedSgIds.add(sg.id));
        }
        break;
      }
    }

    // Always search by SG name directly
    securityGroups
      .filter(sg => {
        const sgName = sg.group_name?.toLowerCase() || '';
        const sgTagName = sg.tags?.Name?.toLowerCase() || '';
        const sgDescription = sg.description?.toLowerCase() || '';
        return (
          sgName.includes(queryLower) ||
          sgTagName.includes(queryLower) ||
          sgDescription.includes(queryLower)
        );
      })
      .forEach(sg => {
        if (type === 'name') {
          const alreadyMatched = result.resolvedFrom.matchedResources.some(
            r => r.type === 'security_group' && r.id === sg.id
          );
          if (!alreadyMatched) {
            result.resolvedFrom.matchedResources.push({
              type: 'security_group',
              id: sg.id,
              name: sg.group_name
            });
          }
        }
        relatedSgIds.add(sg.id);
      });
  }

  relatedSgIds.forEach(id => result.sgIds.push(id));
}

/**
 * Resolves OCI search query to NSG/Security List IDs
 */
function resolveOCISearch(
  query: string,
  queryLower: string,
  type: ResourceSearchType,
  vcns: Record<string, OCIVCN>,
  result: SearchResolutionResult
): void {
  const relatedNsgIds = new Set<string>();

  for (const vcn of Object.values(vcns)) {
    const subnets = vcn.subnets || [];
    const nsgs = vcn.network_security_groups || [];
    const securityLists = vcn.security_lists || [];
    const vnics = vcn.vnics || [];

    switch (type) {
      case 'subnet':
      case 'name': {
        // Find subnets that match by ID, display_name, or CIDR
        const matchingSubnets = subnets.filter(s => {
          const subnetName = s.display_name?.toLowerCase() || '';
          return (
            s.id === query ||
            s.id.toLowerCase() === queryLower ||
            subnetName.includes(queryLower) ||
            s.cidr_block?.includes(query)
          );
        });

        matchingSubnets.forEach(subnet => {
          result.resolvedFrom.matchedResources.push({
            type: 'subnet',
            id: subnet.id,
            name: subnet.display_name
          });
          
          // Also add security_list_ids from the subnet directly
          if (subnet.security_list_ids) {
            subnet.security_list_ids.forEach(slId => relatedNsgIds.add(slId));
          }
        });

        // Find VNICs in those subnets and get their NSGs
        for (const subnet of matchingSubnets) {
          vnics
            .filter(vnic => vnic.subnet_id === subnet.id)
            .forEach(vnic => {
              extractNsgIdsFromOCIVNIC(vnic).forEach(nsgId => relatedNsgIds.add(nsgId));
            });
        }
        break;
      }

      case 'ip': {
        vnics
          .filter(vnic => {
            if (vnic.private_ip_address === query) return true;
            if (vnic.private_ip_addresses) {
              return vnic.private_ip_addresses.some(
                pip => pip.private_ip_address === query
              );
            }
            return false;
          })
          .forEach(vnic => {
            result.resolvedFrom.matchedResources.push({
              type: 'vnic',
              id: vnic.id,
              name: `IP: ${vnic.private_ip_address}`
            });
            extractNsgIdsFromOCIVNIC(vnic).forEach(nsgId => relatedNsgIds.add(nsgId));
            
            // Also find the subnet and add its security lists
            const subnet = subnets.find(s => s.id === vnic.subnet_id);
            if (subnet?.security_list_ids) {
              subnet.security_list_ids.forEach(slId => relatedNsgIds.add(slId));
            }
          });
        break;
      }

      case 'eni': {
        // In OCI, this is a VNIC
        vnics
          .filter(vnic => vnic.id === query || vnic.id.toLowerCase() === queryLower)
          .forEach(vnic => {
            result.resolvedFrom.matchedResources.push({
              type: 'vnic',
              id: vnic.id,
              name: vnic.display_name
            });
            extractNsgIdsFromOCIVNIC(vnic).forEach(nsgId => relatedNsgIds.add(nsgId));
            
            // Also find the subnet and add its security lists
            const subnet = subnets.find(s => s.id === vnic.subnet_id);
            if (subnet?.security_list_ids) {
              subnet.security_list_ids.forEach(slId => relatedNsgIds.add(slId));
            }
          });
        break;
      }

      case 'sg': {
        // In OCI, this is an NSG
        const matchingNsg = nsgs.find(
          nsg => nsg.id === query || nsg.id.toLowerCase() === queryLower
        );
        if (matchingNsg) {
          result.resolvedFrom.matchedResources.push({
            type: 'nsg',
            id: matchingNsg.id,
            name: matchingNsg.display_name
          });
          relatedNsgIds.add(matchingNsg.id);
        }
        break;
      }

      case 'nacl': {
        // In OCI, this is a Security List
        const matchingSL = securityLists.find(
          sl => sl.id === query || sl.id.toLowerCase() === queryLower
        );
        if (matchingSL) {
          result.resolvedFrom.matchedResources.push({
            type: 'security_list',
            id: matchingSL.id,
            name: matchingSL.display_name
          });
          relatedNsgIds.add(matchingSL.id);
          
          // Find all subnets using this security list
          const subnetIds = subnets
            .filter(s => s.security_list_ids?.includes(matchingSL.id))
            .map(s => s.id);
          
          // Find VNICs in those subnets and add their NSGs
          vnics
            .filter(vnic => subnetIds.includes(vnic.subnet_id))
            .forEach(vnic => {
              extractNsgIdsFromOCIVNIC(vnic).forEach(nsgId => relatedNsgIds.add(nsgId));
            });
        }
        break;
      }

      case 'vpc': {
        // In OCI, this is a VCN
        if (vcn.info.id === query || vcn.info.id.toLowerCase() === queryLower) {
          result.resolvedFrom.matchedResources.push({
            type: 'vcn',
            id: vcn.info.id,
            name: vcn.info.display_name
          });
          nsgs.forEach(nsg => relatedNsgIds.add(nsg.id));
          securityLists.forEach(sl => relatedNsgIds.add(sl.id));
        }
        break;
      }
    }

    // Always search by NSG/SL name directly
    nsgs
      .filter(nsg => {
        const nsgName = nsg.display_name?.toLowerCase() || '';
        return nsgName.includes(queryLower);
      })
      .forEach(nsg => {
        if (type === 'name') {
          const alreadyMatched = result.resolvedFrom.matchedResources.some(
            r => (r.type === 'nsg' || r.type === 'network_security_group') && r.id === nsg.id
          );
          if (!alreadyMatched) {
            result.resolvedFrom.matchedResources.push({
              type: 'nsg',
              id: nsg.id,
              name: nsg.display_name
            });
          }
        }
        relatedNsgIds.add(nsg.id);
      });

    // Also search in security lists by name
    securityLists
      .filter(sl => {
        const slName = sl.display_name?.toLowerCase() || '';
        return slName.includes(queryLower);
      })
      .forEach(sl => {
        if (type === 'name') {
          const alreadyMatched = result.resolvedFrom.matchedResources.some(
            r => r.type === 'security_list' && r.id === sl.id
          );
          if (!alreadyMatched) {
            result.resolvedFrom.matchedResources.push({
              type: 'security_list',
              id: sl.id,
              name: sl.display_name
            });
          }
        }
        relatedNsgIds.add(sl.id);
      });
  }

  relatedNsgIds.forEach(id => result.sgIds.push(id));
}

/**
 * Resolves a search query to related Security Group/NSG IDs by traversing
 * the discovery data relationships.
 * 
 * Supports both AWS and OCI discovery formats.
 */
export function resolveToSecurityGroups(
  query: string,
  discovery: DiscoveryData | null
): SearchResolutionResult {
  const result: SearchResolutionResult = {
    sgIds: [],
    resolvedFrom: {
      type: 'name',
      matchedResources: []
    }
  };

  if (!discovery || !query.trim()) {
    return result;
  }

  const queryTrimmed = query.trim();
  const queryLower = queryTrimmed.toLowerCase();
  const type = identifyResourceType(queryTrimmed);
  
  result.resolvedFrom.type = type;

  // Detect cloud provider and resolve accordingly
  const hasAWS = discovery.vpcs && Object.keys(discovery.vpcs).length > 0;
  const hasOCI = discovery.vcns && Object.keys(discovery.vcns).length > 0;

  if (hasAWS && discovery.vpcs) {
    resolveAWSSearch(queryTrimmed, queryLower, type, discovery.vpcs, result);
  }

  if (hasOCI && discovery.vcns) {
    resolveOCISearch(queryTrimmed, queryLower, type, discovery.vcns, result);
  }

  // Remove duplicates
  result.sgIds = Array.from(new Set(result.sgIds));

  return result;
}

/**
 * Generates a human-readable description of what the search resolved to.
 * Used for showing context to the user.
 */
export function getSearchResolutionDescription(
  result: SearchResolutionResult,
  query: string
): string | null {
  if (result.sgIds.length === 0) {
    return null;
  }

  const { type, matchedResources } = result.resolvedFrom;
  
  if (matchedResources.length === 0) {
    return null;
  }

  const sgCount = result.sgIds.length;
  const sgText = sgCount === 1 ? '1 security resource' : `${sgCount} security resources`;

  switch (type) {
    case 'subnet': {
      const subnetName = matchedResources[0]?.name || matchedResources[0]?.id || query;
      return `Showing findings for ${sgText} in subnet ${subnetName}`;
    }
    case 'ip': {
      return `Showing findings for ${sgText} associated with IP ${query}`;
    }
    case 'eni': {
      const resourceType = matchedResources[0]?.type === 'vnic' ? 'VNIC' : 'ENI';
      return `Showing findings for ${sgText} attached to ${resourceType} ${query}`;
    }
    case 'nacl': {
      const resourceType = matchedResources[0]?.type === 'security_list' ? 'Security List' : 'NACL';
      return `Showing findings for ${sgText} related to ${resourceType} ${query}`;
    }
    case 'vpc': {
      const vpcName = matchedResources[0]?.name || query;
      const resourceType = matchedResources[0]?.type === 'vcn' ? 'VCN' : 'VPC';
      return `Showing findings for ${sgText} in ${resourceType} ${vpcName}`;
    }
    case 'sg':
    case 'name':
    default:
      return null;
  }
}
